/**
 * Two-texture ping-pong feedback chain. The previous frame's `current` becomes
 * the next frame's `previous`. Recreated when the internal render size changes.
 *
 * Both textures get RENDER_ATTACHMENT | TEXTURE_BINDING so they can serve as
 * either the destination or the source of the warp pass.
 */

export type FramebufferRing = {
  format: GPUTextureFormat;
  width: number;
  height: number;
  /** Texture we sample from this frame (last frame's output). */
  previous: GPUTexture;
  /** Texture we render into this frame. */
  current: GPUTexture;
  previousView: GPUTextureView;
  currentView: GPUTextureView;
  /** Linear sampler for sampling `previous` with sub-pixel bilinear. */
  linearSampler: GPUSampler;
  /** Convenience for warp UV reads at the edges. */
  clampLinearSampler: GPUSampler;
};

export function createFramebufferRing(
  device: GPUDevice,
  format: GPUTextureFormat,
  width: number,
  height: number,
): FramebufferRing {
  const desc: GPUTextureDescriptor = {
    size: { width, height, depthOrArrayLayers: 1 },
    format,
    usage:
      GPUTextureUsage.RENDER_ATTACHMENT |
      GPUTextureUsage.TEXTURE_BINDING |
      GPUTextureUsage.COPY_SRC,
    label: 'feedback-ring',
  };
  const a = device.createTexture({ ...desc, label: 'feedback-ring-A' });
  const b = device.createTexture({ ...desc, label: 'feedback-ring-B' });

  const linearSampler = device.createSampler({
    label: 'feedback-linear',
    magFilter: 'linear',
    minFilter: 'linear',
    addressModeU: 'repeat',
    addressModeV: 'repeat',
  });
  const clampLinearSampler = device.createSampler({
    label: 'feedback-linear-clamp',
    magFilter: 'linear',
    minFilter: 'linear',
    addressModeU: 'clamp-to-edge',
    addressModeV: 'clamp-to-edge',
  });

  return {
    format,
    width,
    height,
    previous: a,
    current: b,
    previousView: a.createView(),
    currentView: b.createView(),
    linearSampler,
    clampLinearSampler,
  };
}

/** Rotate after a frame: this frame's output becomes next frame's input. */
export function rotateRing(ring: FramebufferRing): void {
  const t = ring.previous;
  ring.previous = ring.current;
  ring.current = t;
  const v = ring.previousView;
  ring.previousView = ring.currentView;
  ring.currentView = v;
}

export function destroyRing(ring: FramebufferRing): void {
  ring.previous.destroy();
  ring.current.destroy();
}

export function recreateRing(
  ring: FramebufferRing,
  device: GPUDevice,
  width: number,
  height: number,
): FramebufferRing {
  destroyRing(ring);
  return createFramebufferRing(device, ring.format, width, height);
}

/**
 * Clears both textures to opaque black. Useful at boot and on resize so the
 * very first frame doesn't sample uninitialized memory.
 */
export function clearRing(device: GPUDevice, ring: FramebufferRing): void {
  const encoder = device.createCommandEncoder({ label: 'clear-feedback-ring' });
  for (const view of [ring.previousView, ring.currentView]) {
    const pass = encoder.beginRenderPass({
      colorAttachments: [
        {
          view,
          clearValue: { r: 0, g: 0, b: 0, a: 1 },
          loadOp: 'clear',
          storeOp: 'store',
        },
      ],
    });
    pass.end();
  }
  device.queue.submit([encoder.finish()]);
}
